import { wrapLines } from "@/lib/zikirCard";
import { shareBlob, downloadBlob } from "@/lib/zikirDoneCard";
import { DAILY_UI as UI } from "@/data/dailyReminders";
import { LANG_TAG } from "@/lib/geo";
import { fill } from "@/lib/utils";
import { logWarn } from "@/lib/log";

// Square (1080×1080) emerald+gold medal card for a consistency badge (7 / 30 consecutive worship days).
const W = 1080;
const PAD = 100;
const GOLD = "#E6C25C";
const GOLD_DEEP = "#D4AF37";
const EMERALD = "#0a3b2b";
const WHITE = "#ffffff";
const SUB = "rgba(255,255,255,0.82)";

async function drawBadge({ tier, title, subtitle, dateLine, name, labels }) {
  try { await document.fonts.ready; } catch (e) { logWarn("fonts not ready, using system fonts:", e); }
  const SERIF = (px, w = "") => `${w} ${px}px "Playfair Display", Georgia, serif`.trim();
  const SANS = (px, w = "") => `${w} ${px}px "Plus Jakarta Sans", Arial, sans-serif`.trim();
  const AR = (px) => `${px}px UthmanicHafs, "Traditional Arabic", Georgia, serif`;

  const canvas = document.createElement("canvas");
  canvas.width = W; canvas.height = W;
  const ctx = canvas.getContext("2d");
  const g = ctx.createLinearGradient(0, 0, W, W);
  g.addColorStop(0, EMERALD); g.addColorStop(0.55, "#0f5a3f"); g.addColorStop(1, EMERALD);
  ctx.fillStyle = g; ctx.fillRect(0, 0, W, W);
  const rg = ctx.createRadialGradient(W / 2, 420, 40, W / 2, 420, 560);
  rg.addColorStop(0, "rgba(230,194,92,0.28)"); rg.addColorStop(1, "rgba(230,194,92,0)");
  ctx.fillStyle = rg; ctx.fillRect(0, 0, W, W);
  ctx.strokeStyle = GOLD_DEEP; ctx.lineWidth = 8; ctx.strokeRect(44, 44, W - 88, W - 88);
  ctx.lineWidth = 2; ctx.strokeRect(64, 64, W - 128, W - 128);

  ctx.textAlign = "center";
  ctx.fillStyle = GOLD; ctx.font = AR(46); ctx.direction = "rtl";
  ctx.fillText("ٱلْحَمْدُ لِلَّٰهِ", W / 2, 150);
  ctx.direction = "ltr";
  ctx.fillStyle = SUB; ctx.font = SANS(24);
  if ("letterSpacing" in ctx) ctx.letterSpacing = "5px";
  ctx.fillText((labels.brand || "Manasik").toUpperCase(), W / 2, 196);
  if ("letterSpacing" in ctx) ctx.letterSpacing = "0px";

  // Ribbon tails
  const cy = 400;
  ctx.fillStyle = GOLD_DEEP;
  for (const s of [-1, 1]) {
    ctx.beginPath();
    ctx.moveTo(W / 2 + s * 40, cy + 120); ctx.lineTo(W / 2 + s * 125, cy + 275);
    ctx.lineTo(W / 2 + s * 80, cy + 275); ctx.lineTo(W / 2 + s * 55, cy + 232); ctx.lineTo(W / 2 + s * 20, cy + 275);
    ctx.lineTo(W / 2 + s * -20, cy + 275); ctx.closePath(); ctx.fill();
  }
  // Medal: laurel ticks, outer ring, inner disc
  ctx.strokeStyle = GOLD; ctx.lineWidth = 6; ctx.lineCap = "round";
  for (let i = 0; i < 36; i++) {
    const a = (i / 36) * Math.PI * 2;
    ctx.beginPath(); ctx.moveTo(W / 2 + Math.cos(a) * 200, cy + Math.sin(a) * 200); ctx.lineTo(W / 2 + Math.cos(a) * 222, cy + Math.sin(a) * 222); ctx.stroke();
  }
  ctx.beginPath(); ctx.arc(W / 2, cy, 180, 0, Math.PI * 2);
  ctx.fillStyle = "rgba(230,194,92,0.16)"; ctx.fill();
  ctx.lineWidth = 8; ctx.strokeStyle = GOLD; ctx.stroke();
  const dg = ctx.createLinearGradient(W / 2 - 140, cy - 140, W / 2 + 140, cy + 140);
  dg.addColorStop(0, "#f3dc8f"); dg.addColorStop(0.5, GOLD); dg.addColorStop(1, GOLD_DEEP);
  ctx.beginPath(); ctx.arc(W / 2, cy, 140, 0, Math.PI * 2); ctx.fillStyle = dg; ctx.fill();
  ctx.fillStyle = EMERALD; ctx.font = SERIF(150, "bold");
  ctx.fillText(String(tier), W / 2, cy + 52);
  ctx.font = SANS(26, "bold");
  if ("letterSpacing" in ctx) ctx.letterSpacing = "4px";
  ctx.fillText((labels.daysWord || "").toUpperCase(), W / 2, cy + 100);
  if ("letterSpacing" in ctx) ctx.letterSpacing = "0px";

  let y = 745;
  ctx.fillStyle = WHITE; ctx.font = SERIF(56, "bold");
  for (const ln of wrapLines(ctx, title, SERIF(56, "bold"), W - PAD * 2).slice(0, 2)) { ctx.fillText(ln, W / 2, y); y += 66; }
  ctx.fillStyle = GOLD; ctx.font = SANS(30, "600");
  ctx.fillText(subtitle, W / 2, y); y += 48;
  ctx.fillStyle = SUB; ctx.font = SANS(26);
  ctx.fillText(dateLine, W / 2, y);

  if (name) { ctx.fillStyle = WHITE; ctx.font = SANS(30, "600"); ctx.fillText(name, W / 2, W - 140); }
  ctx.fillStyle = SUB; ctx.font = SANS(26);
  ctx.fillText(labels.footer || "Manasik — Panduan Umrah & Haji", W / 2, W - 92);

  const blob = await new Promise((r) => canvas.toBlob(r, "image/png"));
  return { blob, url: URL.createObjectURL(blob) };
}

// Build the localized badge card for a tier; `date` = "YYYY-MM-DD" the badge was reached (defaults to today).
export async function makeBadgeCard({ tier, date, name, tc, lang }) {
  const d = date ? new Date(date + "T12:00:00") : new Date();
  const dateLine = fill(tc(UI.badgeEarnedOn), { date: d.toLocaleDateString(LANG_TAG[lang] || "en-US", { day: "numeric", month: "long", year: "numeric" }) });
  const title = fill(tc(UI.badgeName), { n: tier });
  const res = await drawBadge({
    tier, title, subtitle: fill(tc(UI.badgeCardSub), { n: tier }), dateLine, name,
    labels: { brand: "Manasik", daysWord: fill(tc(UI.days), { n: "" }).trim(), footer: `Manasik — ${tc(UI.badgesTitle)}` },
  });
  return { ...res, title, text: fill(tc(UI.badgeShareText), { n: tier }) };
}

export const shareBadgeCard = (card, tier) => shareBlob(card.blob, { title: card.title, text: card.text, filename: `manasik-lencana-${tier}-hari.png` });
export const saveBadgeCard = (card, tier) => downloadBlob(card.blob, `manasik-lencana-${tier}-hari.png`);
